import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuccessIcon, CloseIcon } from '../constants.tsx';

interface FastContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FastContactModal: React.FC<FastContactModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || !email.trim() || !message.trim()) {
            setStatus('error');
            setErrorMessage(t('formErrorRequired') || 'يرجى تعبئة الحقول المطلوبة');
            return;
        }
        setStatus('submitting');
        setErrorMessage('');

        try {
            // Using the existing Resend Worker Endpoint from Support.tsx
            const workerResponse = await fetch('https://tad-contact-worker.itsmybadsoul.workers.dev/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email,
                    phone,
                    message,
                    userId: 'anonymous-fast-modal'
                }),
            });

            if (!workerResponse.ok) {
                setStatus('error');
                setErrorMessage('عذراً، فشل الإرسال، يرجى المحاولة لاحقاً.');
                return;
            }

            setStatus('success');
            
            // Optionally clear fields
            setName('');
            setEmail('');
            setPhone('');
            setMessage('');
            setTimeout(() => {
                onClose();
                setStatus('idle');
            }, 3000); // Close automatically after 3 seconds
        } catch (error) {
            setStatus('error');
            setErrorMessage(t('formErrorGeneric') || 'حدث خطأ غير متوقع.');
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" dir="rtl">
            <div className="relative w-full max-w-lg bg-[#0d152b] rounded-2xl border border-purple-500/30 shadow-xl overflow-hidden p-6 sm:p-8">
                
                <button 
                    onClick={onClose}
                    className="absolute top-4 left-4 p-2 text-gray-400 hover:text-white transition-colors bg-white/5 rounded-full"
                >
                    <CloseIcon />
                </button>

                {status === 'success' ? (
                    <div className="text-center py-8">
                        <div className="flex justify-center mb-6">
                            <SuccessIcon />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-3">تم إرسال رسالتك بنجاح!</h3>
                        <p className="text-gray-400 mb-6">سنتواصل معك في أقرب وقت ممكن.</p>
                        <button 
                            onClick={onClose}
                            className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 px-8 rounded-lg transition-colors"
                        >
                            إغلاق النافذة
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-white mb-2">مراسلة سريعة</h2>
                            <p className="text-sm text-gray-400">لست بحاجة لتسجيل الدخول، فقط اكتب رسالتك وسنرد عليك عبر الإيميل أو الجوال.</p>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-4 text-right">
                            <div>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)} 
                                    placeholder="الاسم الكامل" 
                                    className="w-full bg-[#151f38] border border-gray-700/50 focus:border-purple-500 text-white rounded-lg px-4 py-3 outline-none transition-colors"
                                    required 
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="w-full sm:w-1/2">
                                    <input 
                                        type="email" 
                                        value={email} 
                                        onChange={e => setEmail(e.target.value)} 
                                        placeholder="البريد الإلكتروني" 
                                        className="w-full bg-[#151f38] border border-gray-700/50 focus:border-purple-500 text-white rounded-lg px-4 py-3 outline-none transition-colors"
                                        required 
                                    />
                                </div>
                                <div className="w-full sm:w-1/2">
                                    <input 
                                        type="tel" 
                                        value={phone} 
                                        onChange={e => setPhone(e.target.value)} 
                                        placeholder="رقم الجوال (اختياري / مفضل)" 
                                        className="w-full bg-[#151f38] border border-gray-700/50 focus:border-purple-500 text-white rounded-lg px-4 py-3 outline-none transition-colors"
                                    />
                                </div>
                            </div>
                            <div>
                                <textarea 
                                    value={message} 
                                    onChange={e => setMessage(e.target.value)} 
                                    placeholder="اكتب رسالتك هنا..." 
                                    className="w-full bg-[#151f38] border border-gray-700/50 focus:border-purple-500 text-white rounded-lg px-4 py-3 outline-none transition-colors resize-none h-32"
                                    required 
                                />
                            </div>

                            {status === 'error' && (
                                <p className="text-red-400 text-sm mt-2 text-center">{errorMessage}</p>
                            )}

                            <button 
                                type="submit" 
                                disabled={status === 'submitting'}
                                className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white font-bold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(139,92,246,0.3)] mt-4"
                            >
                                {status === 'submitting' ? 'جاري الإرسال...' : 'إرسال الرسالة'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default FastContactModal;
